import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatErr } from "../lib/api";
import { useI18n } from "../lib/i18n";
import ServiceCard from "../components/ServiceCard";
import { Sparkles, MessageSquare, MapPin, Calendar, Users } from "lucide-react";

export default function DestinationPage({ onOpenConcierge }) {
  const { slug } = useParams();
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [activeCat, setActiveCat] = useState("all");

  useEffect(() => {
    (async () => {
      try {
        setData(null); setError(null);
        const { data } = await api.get(`/destinations/${slug}`);
        setData(data);
      } catch (e) { setError(formatErr(e)); }
    })();
  }, [slug]);

  if (error) return (
    <div className="pt-32 px-6 max-w-3xl mx-auto">
      <div className="text-rose-300">{error}</div>
      <Link to="/" className="btn btn-outline mt-4">{t("common.back", "Home")}</Link>
    </div>
  );
  if (!data) return <div className="pt-32 px-6 text-muted">{t("common.loading", "Loading…")}</div>;

  const { destination: d, categories, services } = data;
  const isActive = d.status === "active";
  const filtered = activeCat === "all" ? services : services.filter((s) => {
    const c = categories.find((cc) => cc.id === s.category_id);
    return c && c.slug === activeCat;
  });
  const catBy = Object.fromEntries(categories.map((c) => [c.id, c]));

  const statusLabel = isActive
    ? t("destinations.bookable_now", "Bookable now")
    : t("destinations.coming_soon_title", "Coming soon");

  return (
    <div className="relative">
      {/* HERO */}
      <section className="relative min-h-[78svh] overflow-hidden">
        <img src={d.hero_media_url} alt={d.name} className="absolute inset-0 w-full h-full object-cover"/>
        <div className="absolute inset-0 bg-gradient-to-b from-ink/30 via-ink/55 to-ink"/>
        <div className="aurora"/>
        <div className="relative pt-32 md:pt-40 pb-20 px-5 md:px-10 mx-auto max-w-7xl">
          <div className="eyebrow mb-4 flex items-center gap-3">
            <span className="h-px w-10 bg-teal"/> {d.country} · {statusLabel}
          </div>
          <h1 className="section-title text-5xl sm:text-7xl lg:text-[96px] text-bone leading-[0.95]">
            {d.hero_title || d.name}.
          </h1>
          {d.hero_subtitle && (
            <p className="mt-5 max-w-2xl text-base md:text-lg text-muted">{d.hero_subtitle}</p>
          )}
          {d.description && (
            <p className="mt-3 max-w-2xl text-sm text-muted/80 leading-relaxed">{d.description}</p>
          )}
          <div className="mt-8 flex flex-wrap gap-3">
            {isActive ? (
              <a href="#catalog" className="btn btn-primary" data-testid="btn-dest-explore">
                {t("destinations.browse_services", "Browse services")} →
              </a>
            ) : (
              <Link to="/" className="btn btn-primary">
                {t("destinations.waitlist_submit", "Join the waitlist")} →
              </Link>
            )}
            <button onClick={onOpenConcierge} className="btn btn-outline" data-testid="btn-dest-concierge">
              <MessageSquare size={16}/> {t("destinations.talk_to_concierge", "Talk to concierge")}
            </button>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      {isActive && (
        <section id="catalog" className="relative px-5 md:px-10 mx-auto max-w-7xl py-20 md:py-28">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
            <div>
              <div className="eyebrow">{t("destinations.curated_categories", "Curated categories")}</div>
              <h2 className="section-title text-3xl md:text-5xl mt-2">{t("destinations.what_would_you_like", "What would you like to book?")}</h2>
            </div>
            <div className="text-sm text-muted">
              {t("destinations.showing_count", "{{count}} of {{total}}", { count: filtered.length, total: services.length })} · {t("home.trust_verified_title", "Verified providers")}
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mb-10" data-testid="category-filter">
            <button className={`tab-pill ${activeCat === "all" ? "active" : ""}`} onClick={() => setActiveCat("all")} data-testid="cat-all">
              {t("destinations.filter_all", "All")}
            </button>
            {categories.map((c) => (
              <button key={c.id} className={`tab-pill ${activeCat === c.slug ? "active" : ""}`} onClick={() => setActiveCat(c.slug)} data-testid={`cat-${c.slug}`}>{c.name}</button>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {filtered.map((s) => (
              <ServiceCard key={s.id} s={s} category={catBy[s.category_id]}/>
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="luxe-card p-10 text-center">
              <Sparkles className="mx-auto text-teal mb-3"/>
              <div className="font-serif-display text-2xl">{t("destinations.no_services_title", "Catalog launching soon")}</div>
              <div className="text-sm text-muted mt-1 max-w-md mx-auto">
                {t("destinations.no_services_body", "Our concierge is curating verified providers for {{name}}. Tell us what you need.", { name: d.name })}
              </div>
              <button onClick={onOpenConcierge} className="btn btn-primary mt-5">
                {t("destinations.describe_what_you_need", "Describe what you need")}
              </button>
            </div>
          )}
        </section>
      )}

      {/* HOW IT WORKS — small */}
      <section className="relative px-5 md:px-10 mx-auto max-w-7xl py-24">
        <div className="grid md:grid-cols-4 gap-4">
          {[
            { n: "01", t: t("home.how_step_1_title", "Choose."), d: t("destinations.step_01", "Pick a service or describe what you want."), i: <Sparkles size={18}/> },
            { n: "02", t: t("home.how_step_2_title", "Confirm."), d: t("destinations.step_02", "We check with the verified provider directly."), i: <MapPin size={18}/> },
            { n: "03", t: t("home.how_step_3_title", "Pay deposit."), d: t("destinations.step_03", "Crypto or card. Held as a protected deposit."), i: <Calendar size={18}/> },
            { n: "04", t: t("home.how_step_4_title", "Enjoy."), d: t("destinations.step_04", "Confirm completion. Funds release. Review."), i: <Users size={18}/> },
          ].map((s) => (
            <div key={s.n} className="luxe-card p-6">
              <div className="text-teal mb-4">{s.i}</div>
              <div className="font-script text-teal text-2xl">{s.n}</div>
              <div className="font-serif-display text-2xl text-bone mt-1">{s.t}</div>
              <div className="text-sm text-muted mt-2">{s.d}</div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
